import { Activity, Database, Clock, AlertCircle, TrendingUp } from "lucide-react";
import { StatsCard } from "@/components/dashboard/StatsCard";
import { ActiveJobsCard } from "@/components/dashboard/ActiveJobsCard";
import { RecentOutputsCard } from "@/components/dashboard/RecentOutputsCard";
import { AuditLogCard } from "@/components/dashboard/AuditLogCard";
import { DataCoverageCard } from "@/components/dashboard/DataCoverageCard";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@repo/ui/badge";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useSnapshotMetrics } from "@/hooks/useSnapshotMetrics";

const Dashboard = () => {
  const { isViewer, isOperator, isAdmin } = useAuth();
  const { stats, isLoading } = useDashboardStats();
  const { summary: snapshotSummary, isLoading: snapshotLoading } = useSnapshotMetrics();

  const getRoleLabel = () => {
    if (isAdmin) return { label: "Admin", className: "bg-primary/15 text-primary" };
    if (isOperator) return { label: "Operator", className: "bg-accent/15 text-accent" };
    if (isViewer) return { label: "Viewer", className: "bg-muted text-muted-foreground" };
    return null;
  };

  const roleInfo = getRoleLabel();
  const snapshotDeltaLabel =
    snapshotSummary.deltaVsPrevious30d === null
      ? "No prior 30d baseline yet"
      : `${snapshotSummary.deltaVsPrevious30d >= 0 ? "+" : ""}${snapshotSummary.deltaVsPrevious30d.toFixed(2)} vs previous 30d`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of your property data scraping operations
          </p>
        </div>
        {roleInfo && (
          <Badge className={roleInfo.className}>
            {roleInfo.label} Access
          </Badge>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatsCard
          title="Active Jobs"
          value={isLoading ? "..." : stats.activeJobs}
          icon={Activity}
          variant="accent"
        />
        <StatsCard
          title="Adapters"
          value={isLoading ? "..." : stats.totalAdapters}
          icon={Database}
          variant="default"
        />
        <StatsCard
          title="Files Stored"
          value={isLoading ? "..." : stats.filesStored}
          icon={Clock}
          variant="default"
        />
        <StatsCard
          title="Failed (24h)"
          value={isLoading ? "..." : stats.failedJobs24h}
          icon={AlertCircle}
          variant="destructive"
        />
        <StatsCard
          title="Avg snapshots / property (suburb)"
          value={
            snapshotLoading
              ? "..."
              : snapshotSummary.avgSnapshotsPerPropertyPerSuburb.toFixed(2)
          }
          subtitle={snapshotLoading ? undefined : snapshotDeltaLabel}
          icon={TrendingUp}
          variant="default"
        />
      </div>

      <DataCoverageCard />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActiveJobsCard />
        <RecentOutputsCard />
      </div>

      <AuditLogCard />
    </div>
  );
};

export default Dashboard;
